/**
 * Payment Result Page — polls payment status after redirect from Payme/Click.
 * Shows success/pending/failed state with appropriate messaging.
 */

import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@turnifly/shared/ui'
import { usePaymentStatus, paymentKeys } from '@turnifly/shared/hooks'
import { useAuthStore } from '@turnifly/shared/stores'
import { triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'

const POLL_TIMEOUT_MS = 300_000 // 5 minutes — Click can take 1-2 min

export const PaymentResultPage: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const userId = useAuthStore((s) => s.user?.id)

  const paymentId = searchParams.get('payment_id')
  const [shouldPoll, setShouldPoll] = useState(true)

  const { data: statusData } = usePaymentStatus(
    paymentId ?? undefined,
    shouldPoll,
  )

  const status = statusData?.status ?? 'pending'

  // Stop polling when we get a final state
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (status === 'completed' || status === 'failed' || status === 'cancelled') {
      setShouldPoll(false)
      if (status === 'completed') {
        hapticNotification('success')
        // Invalidate subscription cache so profile shows updated state
        if (userId) {
          void queryClient.invalidateQueries({ queryKey: paymentKeys.subscription(userId) })
        }
      } else {
        hapticNotification('error')
      }
    }
  }, [status, userId, queryClient])
  /* eslint-enable react-hooks/set-state-in-effect */

  // Auto-stop polling after 5 minutes
  useEffect(() => {
    const timer = setTimeout(() => setShouldPoll(false), POLL_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [])

  const handleGoBack = (): void => {
    // Always invalidate subscription on exit — webhook may have updated it
    if (userId) {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.subscription(userId) })
    }
    navigate('/profile', { replace: true })
  }

  if (!paymentId) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center p-6 bg-bg">
        <XCircle size={56} className="text-danger mb-4" />
        <h2 className="text-xl font-display font-bold text-text mb-2">
          {t('premium.paymentFailed')}
        </h2>
        <Button variant="primary" onClick={handleGoBack} className="mt-6">
          {t('premium.goBack')}
        </Button>
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center p-6 bg-bg">
      {status === 'completed' && (
        <>
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mb-6">
            <CheckCircle2 size={48} className="text-success" />
          </div>
          <h2 className="text-xl font-display font-bold text-text mb-2">
            {t('premium.paymentSuccess')}
          </h2>
          <p className="text-sm text-text-hint text-center mb-8">
            {t('premium.paymentSuccessDesc')}
          </p>
        </>
      )}

      {(status === 'failed' || status === 'cancelled') && (
        <>
          <div className="w-20 h-20 rounded-full bg-danger/10 flex items-center justify-center mb-6">
            <XCircle size={48} className="text-danger" />
          </div>
          <h2 className="text-xl font-display font-bold text-text mb-2">
            {t('premium.paymentFailed')}
          </h2>
          <p className="text-sm text-text-hint text-center mb-8">
            {t('premium.paymentFailedDesc')}
          </p>
        </>
      )}

      {(status === 'pending' || status === 'processing') && (
        <>
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
            <Loader2 size={48} className="text-primary animate-spin" />
          </div>
          <h2 className="text-xl font-display font-bold text-text mb-2">
            {t('premium.paymentPending')}
          </h2>
          <p className="text-sm text-text-hint text-center mb-8 leading-relaxed">
            {t('premium.paymentPendingDesc')}
          </p>
        </>
      )}

      <Button variant="primary" fullWidth onClick={handleGoBack}>
        {t('premium.goBack')}
      </Button>
    </div>
  )
}
